import { Storage } from './lib/storage';
import { useState, useEffect, Suspense, lazy } from 'react';
import { HelpCircle, AlertTriangle, Bike, AlertOctagon, Wrench, Map, LogOut, Crown, Store, Settings, Loader2, Bell, X, MessageSquare, MessageCircle, CheckCircle, Trash2, PackageOpen , Users, User, ShoppingBag, Lock, Layout } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import LoginScreen from './screens/LoginScreen';
import type { Role } from './screens/LoginScreen';
import { supabase } from './lib/supabase';
import { COUNTRIES_CONFIG } from './lib/countryConfig';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

import OnboardingScreen from './screens/OnboardingScreen';
import ExpedienteScreen from './screens/ExpedienteScreen';
const SOSScreen = lazy(() => import('./screens/SOSScreen'));
const TallerScreen = lazy(() => import('./screens/TallerScreen'));
const DestinosScreen = lazy(() => import('./screens/DestinosScreen'));
const ParcheScreen = lazy(() => import('./screens/ParcheScreen'));
const MercadoScreen = lazy(() => import('./screens/MercadoScreen'));
const ConfiguracionScreen = lazy(() => import('./screens/ConfiguracionScreen'));
const MembresiaScreen = lazy(() => import('./screens/MembresiaScreen'));
const SOSConfigModal = lazy(() => import('./screens/SOSConfigModal'));
const ApodosModal = lazy(() => import('./screens/ApodosModal'));
const AyudaScreen = lazy(() => import('./screens/AyudaScreen'));
const ValidarScreen = lazy(() => import('./screens/ValidarScreen'));
const MotomarketScreen = lazy(() => import('./screens/MotomarketScreen'));
const MuroBikerScreen = lazy(() => import('./screens/MuroBikerScreen'));

function ScreenLoader() {
  return (
    <div className="flex flex-col items-center justify-center p-10 h-full w-full bg-transparent">
      <Loader2 className="w-8 h-8 text-primary-cyan animate-spin opacity-60 mb-2" />
      <span className="text-white/40 text-[10px] font-black tracking-widest uppercase">Cargando Módulo...</span>
    </div>
  );
}
function App() {
  const [role, setRole] = useState<Role>(() => {
    try {
      const saved = Storage.get('motopikis_user');
      if (saved) {
        const u = JSON.parse(saved);
        // Aseguramos sincronía total del ID para el Garaje Offline
        if (u.id) Storage.set('motopikis_user_id', u.id);
        return u.rol || null;
      }
    } catch(e) {}
    return null;
  });

  const [activeTab, setActiveTab] = useState<'onboarding' | 'expediente' | 'sos' | 'mercado' | 'taller' | 'destinos' | 'motomarket' | 'parche' | 'murobiker'>(() => {
    try {
      const saved = Storage.get('motopikis_user');
      const mode = Storage.get('motopikis_login_mode');
      if (saved) {
        const u = JSON.parse(saved);
        if (u.rol === 'empleado_taller' || (u.rol === 'motero_dueño' && mode === 'business')) {
           return 'taller';
        }
        return 'expediente';
      }
    } catch(e) {}
    return 'onboarding';
  });
  const [loginMode, setLoginMode] = useState(() => Storage.get('motopikis_login_mode'));
  const [showingMembresia, setShowingMembresia] = useState(false);
  const [showingSOSConfig, setShowingSOSConfig] = useState(false);
  const [showingApodos, setShowingApodos] = useState(false);
  const [showingAyuda, setShowingAyuda] = useState(false);
  const [appTheme, setAppTheme] = useState(() => Storage.get('motopikis_theme') || 'oled');
  const [globalSOS, setGlobalSOS] = useState<any>(null);

  // Estados para Bandeja de Ofertas
  const [mostrarBandejaOfertas, setMostrarBandejaOfertas] = useState(false);
  const [ofertas, setOfertas] = useState<any[]>([]);
  const [solicitudesActivas, setSolicitudesActivas] = useState<any[]>([]);
  const [popupOferta, setPopupOferta] = useState<any>(null);
  // ESTADOS DE AUDITORIA B2B
  const [modalAuditoria, setModalAuditoria] = useState<any>(null);
  const [auditMontoLibre, setAuditMontoLibre] = useState(false);
  const [auditValorManual, setAuditValorManual] = useState('');
  
  const [esOroApp, setEsOroApp] = useState(false);
  const [avatarTabUrl, setAvatarTabUrl] = useState(() => Storage.get('motopikis_avatar_url') || '');
  const [mostrarModalOro, setMostrarModalOro] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    const handleUpdate = (e: any) => {
      setSwRegistration(e.detail);
    };
    window.addEventListener('swUpdate', handleUpdate);
    return () => window.removeEventListener('swUpdate', handleUpdate);
  }, []);

  useEffect(() => {
    const h = () => setShowingMembresia(true);
    document.addEventListener('abrirMembresiaOro', h);
    
    const iv = setInterval(() => {
      try {
        const u = Storage.get('motopikis_user');
        if (u) {
          const parsed = JSON.parse(u);
          setEsOroApp(!!parsed.es_oro);
        }
        setAvatarTabUrl(Storage.get('motopikis_avatar_url') || '');
      } catch(e) {}
    }, 1500);
    return () => {
       clearInterval(iv);
       document.removeEventListener('abrirMembresiaOro', h);
    };
  }, []);

  // Estado para alertas del TALLER (licitaciones pendientes)
  const [alertasTaller, setAlertasTaller] = useState(0);

  // ESTADO GLOBAL: Invitación Deep Link a Rodadas
  const [rutaAConfirmar, setRutaAConfirmar] = useState<any>(null);

  // ESTADO GLOBAL: Notificaciones Push (Anuncios In-App)
  const [anuncioInApp, setAnuncioInApp] = useState<any>(null);
  const [bloqueoMiSOS, setBloqueoMiSOS] = useState<any>(null);

  // ESTADO GLOBAL: Auto-apertura de Anuncio por Deep Link
  const [adAVisitar, setAdAVisitar] = useState<string | null>(null);

  const cancelarMiSOSPendiente = async () => {
     if (!bloqueoMiSOS) return;
     const myId = Storage.get('motopikis_user_id');
     // Cierra TODAS las actvas fantasmas
     await supabase.from('alertas_sos').update({ estado: 'RESUELTA' }).eq('usuario_id', myId).eq('estado', 'ACTIVA');
     setBloqueoMiSOS(null);
  };

  useEffect(() => {
    const initBilling = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          // Bajamos el nivel de log para producción
          await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });
          
          const apiKey = "goog_vYjEYggiCKdbgmoeULfBHXoCrsh";
          await Purchases.configure({ apiKey });
          console.log("✅ RevenueCat: Inicializado correctamente");
        } catch (e: any) {
          console.error("❌ RevenueCat: Error al inicializar", e);
        }
      }
    };
    initBilling();
    
    // Escuchar si el taller ignora o elimina la licitacion desde TallerScreen
    const hideAlert = () => setAlertasTaller(0);
    window.addEventListener('licitacionOcultada', hideAlert);
    
    // Escuchar cuando el taller cierra oficialmente una venta/reparación
    const handleCierre = (e: any) => setModalAuditoria(e.detail);
    window.addEventListener('licitacionCerradaTaller', handleCierre);

    return () => {
      window.removeEventListener('licitacionOcultada', hideAlert);
      window.removeEventListener('licitacionCerradaTaller', handleCierre);
    };
  }, []);

  useEffect(() => {
    if (!role) return;
    const myId = Storage.get('motopikis_user_id');
    if (!myId) return;

    // Sincronizar estado real del usuario (Ej: Si le activan Membresía desde Pikis Hub)
    const syncUser = async () => {
      try {
        const { data } = await supabase.from('usuarios').select('es_oro, vencimiento_oro, rol').eq('id', myId).single();
        if (data) {
          const uStr = Storage.get('motopikis_user');
          if (uStr) {
            const u = JSON.parse(uStr);
            
            // Validación de Vencimiento
            let activeOro = !!data.es_oro;
            if (data.vencimiento_oro && new Date(data.vencimiento_oro) < new Date()) {
                activeOro = false;
            }
            
            u.es_oro = activeOro;
            // No pisar el rol del empleado con el perfil del jefe
            if (role !== 'empleado_taller') {
               u.rol = data.rol;
            }
            Storage.set('motopikis_user', JSON.stringify(u));
          }
        }
      } catch (e) {
        console.error("Error sync user info:", e);
      }
    };
    syncUser();

    // Sincronización MÁGICA en Tiempo Real con Supabase (WebSockets)
    const userChannel = supabase.channel(`public:usuarios:${myId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'usuarios', filter: `id=eq.${myId}` }, (payload) => {
         const newData = payload.new;
         if (newData) {
            const uStr = Storage.get('motopikis_user');
            if (uStr) {
               const u = JSON.parse(uStr);
               
               // Validación de Vencimiento en Tiempo Real
               let activeOro = !!newData.es_oro;
               if (newData.vencimiento_oro && new Date(newData.vencimiento_oro) < new Date()) {
                   activeOro = false;
               }
               
               u.es_oro = activeOro;
               if (role !== 'empleado_taller') {
                  u.rol = newData.rol;
               }
               Storage.set('motopikis_user', JSON.stringify(u));
               // Informamos a todo el celular (SOS, Destinos, Header) del cambio radical
               window.dispatchEvent(new Event('storage'));
            }
         }
      })
      .subscribe();

    let cachedTallerId: string | null = null;
    let cachedRating: number = -1;

    const fetchOfertas = async () => {
      try {
        const { data } = await supabase.from('cotizaciones')
          .select('*')
          .eq('motero_id', myId)
          .in('estado', ['abierta', 'respondida', 'ejecutada', 'pagada'])
          .order('created_at', { ascending: false });
          
        if (data) {
           const actData = data.filter(d => d.estado === 'abierta');
           const resData = data.filter(d => d.estado === 'respondida');
           
           // Buscar si el motero tiene un servicio post-pago o ejecutado pendiente de auditar
           setModalAuditoria((prev: any) => {
              if (prev) return prev; // If already open, don't re-trigger abruptly
              const pendiente = data.find(d => (d.estado === 'ejecutada' || d.estado === 'pagada') && d.auditor_motero === false);
              if (pendiente) {
                 return {
                    id: pendiente.id,
                    taller: pendiente.taller_nombre || 'Taller Aliado',
                    precio: pendiente.valor_final || pendiente.precio_oferta || 0
                 };
              }
              return null;
           });
           
           setSolicitudesActivas(actData);
           
           const mappedOfertas = resData.map((o: any) => ({
              id: o.id,
              taller: o.taller_nombre || 'Taller Aliado',
              taller_id: o.taller_id,
              precio: o.precio_oferta || 0,
              detalle: `Tiempo estimado: ${o.tiempo_oferta || 'No especificado'}`,
              fecha: o.created_at ? new Date(o.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '',
              leido: false
           }));
           
           setOfertas(prev => {
               // Only inject entirely new ones, preserve read state
               return mappedOfertas.map((newOf: any) => {
                   const old = prev.find(p => p.id === newOf.id);
                   return old ? { ...newOf, leido: old.leido } : newOf;
               });
           });
        }

        // Si soy Taller, buscar cuántas licitaciones abiertas hay para mi
        if (role === 'motero_dueño' || role === 'empleado_taller') {
           if (!cachedTallerId) {
               const { data: myTaller } = await supabase.from('talleres_aliados').select('id, rating').eq('usuario_id', myId).limit(1).maybeSingle();
               if (myTaller) {
                   cachedTallerId = myTaller.id;
                   cachedRating = myTaller.rating;
               }
           }
           
           if (cachedTallerId && cachedRating >= 0) {
              const { count } = await supabase.from('cotizaciones')
                 .select('*', { count: 'exact', head: true })
                 .eq('estado', 'abierta')
                 .or(`taller_id.is.null,taller_id.eq.${cachedTallerId}`);
              setAlertasTaller(count || 0);
           }
        }

        if (!anuncioInApp) {
            const userStr = Storage.get('motopikis_user');
            if (userStr) {
                const userObj = JSON.parse(userStr);
                if (userObj.celular) {
                    const { data: notifData } = await supabase.from('notificaciones_inapp').select('*').eq('receptor_id', userObj.celular).eq('leido', false).limit(1).maybeSingle();
                    if (notifData) {
                        setAnuncioInApp(notifData);
                        await supabase.from('notificaciones_inapp').update({ leido: true }).eq('id', notifData.id);
                    }
                }
            }
        }
      } catch(e) {}
    };

    fetchOfertas();
    const iv = setInterval(fetchOfertas, 30000);
    return () => { 
      clearInterval(iv);
      if (userChannel) supabase.removeChannel(userChannel);
    };
  }, [role]);

  // Hook Global: Capturar Invitación Deep Link a Rutas (?unirse=123)
  useEffect(() => {
    const checkInviteLink = async () => {
       const params = new URLSearchParams(window.location.search);
       const inviteId = params.get('unirse');
       if (inviteId) {
          // Limpiamos la URL para mantener la sesión limpia
          window.history.replaceState({}, document.title, window.location.pathname);
          const { data } = await supabase.from('rutas').select('*').eq('id', inviteId).single();
          if (data) {
             setRutaAConfirmar(data);
          }
       }

       // 2. DETECCIÓN DE ANUNCIO DEL MERCADO VIP (?mode=market&ad=123)
       const adId = params.get('ad');
       if (adId && params.get('mode') === 'market') {
          window.history.replaceState({}, document.title, window.location.pathname);
          // Guardamos en memoria persistente para el efecto reactivo de rol
          Storage.set('motopikis_pending_ad', adId);
       }
    };
    
    checkInviteLink();
    
    // Escuchar cuando el celular vuelve a abrir la app (Ej: Desde WhatsApp)
    const handleVis = () => { if (document.visibilityState === 'visible') checkInviteLink(); };
    window.addEventListener('visibilitychange', handleVis);
    
    // Polling extremo: algunos navegadores Android o PWAs no disparan eventos al reanudar
    const urlInterval = setInterval(checkInviteLink, 1500);

    const customEventTrigger = (e: any) => { if(e.detail) setRutaAConfirmar(e.detail); };
    window.addEventListener('abrirInvRutaGlobal', customEventTrigger as any);

    return () => {
       window.removeEventListener('visibilitychange', handleVis);
       window.removeEventListener('abrirInvRutaGlobal', customEventTrigger as any);
       clearInterval(urlInterval);
    };
  }, []);

  // SINCRONIZACIÓN DE DEEP LINK: Se ejecuta cuando el ROL está listo
  useEffect(() => {
    if (!role) return;
    const pendingAd = Storage.get('motopikis_pending_ad');
    if (pendingAd) {
       setAdAVisitar(pendingAd);
       setActiveTab('motomarket');
       Storage.remove('motopikis_pending_ad');
    }
  }, [role]);

  const handleConfirmarAsistencia = async () => {
     if (!rutaAConfirmar) return;
     const localId = Storage.get('motopikis_user_id');
     if (!localId) { alert("⚠️ Para unirte a una ruta debes iniciar sesión primero."); return; }
     
     const { data: userMoto } = await supabase.from('vehiculos').select('placa').eq('usuario_id', localId).single();
     const userObj = JSON.parse(Storage.get('motopikis_user') || '{}');
     
     await supabase.from('asistentes_rutas').upsert({
        ruta_id: rutaAConfirmar.id,
        motero_id: localId,
        nombre: userObj.nombre || 'Piloto',
        placa: (userObj.apodo_moto || userMoto?.placa || 'N/A').toUpperCase(),
     }, { onConflict: 'ruta_id,motero_id' });
     
     // Redirigir y cerrar: Llevar al usuario de regreso a la Mesa Redonda
     setActiveTab('parche');
     setTimeout(() => {
        setRutaAConfirmar(null);
     }, 300);
  };

  const unreadCount = ofertas.filter(o => !o.leido).length;

  const contactarWhatsApp = async (oferta: any) => {
     if (!oferta.taller_id) return alert("Esta cotización no tiene perfil asociado.");
     try {
       const { data: taller } = await supabase.from('talleres_aliados').select('usuario_id, nombre').eq('id', oferta.taller_id).single();
       if (!taller) return alert("No se encontró el perfil de este taller en la base de datos.");
       const { data: user } = await supabase.from('usuarios').select('celular, pais').eq('id', taller.usuario_id).single();
       if (!user || !user.celular) return alert("El taller no tiene un número celular registrado.");
       
       let telefono = user.celular.replace(/\D/g, '');
       const prefix = (user.pais && COUNTRIES_CONFIG[user.pais]) ? COUNTRIES_CONFIG[user.pais].phonePrefix : '57';
       if (!telefono.startsWith(prefix)) telefono = prefix + telefono;
       
       const mensaje = `Hola *${taller.nombre}*, vengo de *MOTOPikis Pro* 🏍️🛡️ y quiero aceptar su cotización de $${oferta.precio.toLocaleString('es-CO')}. Quedo atento para coordinar el servicio. Gracias.\n\n🌐 https://pikishub.com/motopikispro`;
       window.open(`https://wa.me/${telefono}?text=${encodeURIComponent(mensaje)}`, '_blank');
     } catch(e) { alert("Hubo un error de conexión."); }
  };

  const verFicha = async (oferta: any) => {
     if (!oferta.taller_id) return alert("Este taller no cuenta con Ficha Oficial.");
     try {
       const { data: taller } = await supabase.from('talleres_aliados').select('*').eq('id', oferta.taller_id).single();
       if (taller) {
          setPopupOferta({ tipo: 'ficha', data: taller });
       }
     } catch(e) {}
  };

  const eliminarCotizacion = async (e: any, ofertaId: string) => {
     e.stopPropagation();
     if (confirm("¿Estás seguro de descartar y eliminar esta cotización de tu bandeja definitivamente?")) {
        await supabase.from('cotizaciones').delete().eq('id', ofertaId);
        setOfertas(ofertas.filter(o => o.id !== ofertaId));
     }
  };

  // Escuchar Alertas Globales S.O.S
  useEffect(() => {
    if (!role) return;
    const myId = Storage.get('motopikis_user_id');
    let sub: any = null;

    const getLast10 = (phone: string) => {
        if (!phone) return '';
        const justNumbers = String(phone).replace(/\D/g, '');
        return justNumbers.length >= 10 ? justNumbers.slice(-10) : justNumbers;
    };

    const parseRescueList = (val: any): string[] => {
       if (!val) return [];
       if (Array.isArray(val)) return val;
       if (typeof val === 'string') {
          return val.replace(/^{|}$/g, '').split(',').map(s => s.trim().replace(/"/g, ''));
       }
       return [];
    };

    const initializeSOS = async () => {
        let myPhone = '';
        const userStr = Storage.get('motopikis_user');
        if (userStr) {
           try { myPhone = JSON.parse(userStr).celular || ''; } catch(e) {}
        }
        
        if (myId) {
            const { data: dbUser } = await supabase.from('usuarios').select('celular').eq('id', myId).single();
            if (dbUser && dbUser.celular) myPhone = dbUser.celular;
        }

        const myPhone10 = getLast10(myPhone);

        // Fetch activas inicial
        const fetchActivas = async () => {
          const myIdSOS = Storage.get('motopikis_user_id');
          try {
              const { data: miSOSData } = await supabase.from('alertas_sos').select('id').eq('estado', 'ACTIVA').eq('usuario_id', myIdSOS).limit(1);
              if (miSOSData && miSOSData.length > 0) setBloqueoMiSOS(miSOSData[0]);
          } catch(e) {}

          const { data } = await supabase.from('alertas_sos')
             .select('*')
             .eq('estado', 'ACTIVA')
             .neq('usuario_id', myId);
             
          if (data && data.length > 0) {
              const dirigidaAMi = data.find(alerta => {
                 const arr = parseRescueList(alerta.celulares_rescate);
                 return arr.some((c: string) => getLast10(c) === myPhone10);
              });
              if (dirigidaAMi) {
                 const { data: ud } = await supabase.from('usuarios').select('nombre, alias, celular').eq('id', dirigidaAMi.usuario_id).maybeSingle();
                 setGlobalSOS({ ...dirigidaAMi, usuarios: ud });
              }
          }
        };
        fetchActivas();

        sub = supabase.channel('canal-sos')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'alertas_sos' }, async (rawPayload: any) => {
              const payload = rawPayload as { new: any, old: any };
              
              if (payload.new && payload.new.estado === 'ACTIVA' && payload.new.usuario_id !== myId) {
                   const rescueList = parseRescueList(payload.new.celulares_rescate);
                   // alert(`[SISTEMA DETECTA ALERTA DE OTRO USUARIO]\n\nDestinos S.O.S enviados: ${rescueList.join(', ')}\n\nTu número registrado en la red es: ${myPhone10}`);
                   if (rescueList.some((c: string) => getLast10(c) === myPhone10)) {
                       const { data: ud } = await supabase.from('usuarios').select('nombre, alias, celular').eq('id', payload.new.usuario_id).single();
                       setGlobalSOS({ ...payload.new, usuarios: ud });
                   }
              } else if (payload.new && payload.new.estado === 'RESUELTA') {
                   setGlobalSOS((prev: any) => prev && prev.id === payload.new.id ? null : prev);
              }
          }).subscribe();
    };

    initializeSOS();

    return () => { if (sub) supabase.removeChannel(sub); };
  }, [role]);

  useEffect(() => {
    Storage.set('motopikis_theme', appTheme);
  }, [appTheme]);

  // Scroll automático hacia arriba al cambiar de pestaña
  useEffect(() => {
    const scrollBody = document.getElementById('scroll-body');
    if (scrollBody) {
      scrollBody.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  // Persistencia de Sesión (Ya inicializada sincrónicamente, pero mantenemos para actualizaciones raras)
  useEffect(() => {
    const savedUserStr = Storage.get('motopikis_user');
    if (savedUserStr && !role) {
      try {
        const user = JSON.parse(savedUserStr);
        if (user && user.rol) {
          setRole(user.rol);
          setActiveTab(user.rol === 'empleado_taller' ? 'taller' : 'expediente');
        }
      } catch (e) {}
    }
  }, [role]);

  const getThemeBackground = () => {
    switch(appTheme) {
      case 'gris': return 'bg-[#475569]';
      case 'titanio': return 'bg-[#0a1128]';
      case 'amatista': return 'bg-[#291322]';
      case 'militar': return 'bg-[#111f14]';
      case 'oled':
      default: return 'bg-bg-dark';
    }
  };

  const handleLogin = (selectedRole: Role, isNew: boolean = true) => {
    setRole(selectedRole);
    // Sincronizar el modo de login (Negocio vs Motero) al entrar
    const currentMode = Storage.get('motopikis_login_mode');
    setLoginMode(currentMode);
    
    if (isNew) {
      if (selectedRole === 'motero' || selectedRole === 'motero_dueño') setActiveTab('onboarding');
    } else {
      // Redirección INTELIGENTE inmediata para evitar parpadeos
      if (selectedRole === 'motero_dueño' && currentMode === 'business') {
        setActiveTab('taller');
      } else {
        setActiveTab('expediente'); // Salta el Onboarding y va directo al Garaje
      }
    }
    
    if (selectedRole === 'empleado_taller') {
      setActiveTab('taller');
    }
  };

  const handleTabChange = (targetTab: any) => {
    if (role === 'motero' || role === 'motero_dueño') {
      const userStr = Storage.get('motopikis_user');
      const user = userStr ? JSON.parse(userStr) : null;

      if (targetTab !== 'onboarding' && (!user || !user.pin)) {
        alert("⚠️ Acción Requerida: Para acceder a esta sección primero completa Tu Perfil y crea tu PIN");
        setActiveTab('onboarding');
        return;
      }
    }
    setActiveTab(targetTab);
  };

  const logout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    Storage.remove('motopikis_user');
    Storage.remove('motopikis_user_id');
    Storage.remove('motopikis_temp_phone');
    Storage.remove('motopikis_login_mode'); // Limpiar modo negocio al salir
    setRole(null);
    setLoginMode(null);
    setShowLogoutConfirm(false);
  };

  // ROUTE BYPASS: PÁGINA PÚBLICA DE VALIDACIÓN (No requiere Login)
  if (window.location.search.includes('validar=') || window.location.search.includes('serial=')) {
     return (
        <div className={`mobile-simulator bg-bg-dark text-text-main h-full relative overflow-hidden`}>
           <Suspense fallback={<ScreenLoader />}>
              <ValidarScreen />
           </Suspense>
        </div>
     );
  }

  // Si no hay rol, obliga a ver la pantalla de Login
  if (!role) {
    return (
      <div className="mobile-simulator bg-bg-dark text-text-main h-full">
        <LoginScreen onLogin={handleLogin} />
      </div>
    );
  }

  return (
    <div className={`mobile-simulator ${getThemeBackground()} text-text-main h-full relative transition-colors duration-500 overflow-hidden`}>
    
      {/* BANNER S.O.S GLOBAL (IN-APP) */}
      {globalSOS && (
        <div className="absolute top-0 left-0 w-full z-[100] bg-red-600 border-b-4 border-red-900 shadow-[0_0_30px_rgba(239,68,68,0.8)] p-5 pb-6">
          <button onClick={() => setGlobalSOS(null)} className="absolute top-2 right-3 p-2 bg-black/20 hover:bg-black/40 rounded-full transition-colors" title="Ocultar esta alerta localmente">
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="flex items-center gap-4 mt-2">
             <AlertOctagon className="w-10 h-10 text-white animate-pulse shrink-0" />
             <div className="flex-1">
               <h3 className="text-white font-black uppercase text-sm tracking-wider pr-6">¡ALERTA S.O.S RECIBIDA!</h3>
               <p className="text-white/90 text-[13px] font-bold leading-tight mt-1.5 mb-1">
                 Un Motero ({globalSOS.usuarios?.nombre || globalSOS.usuarios?.alias || 'Desconocido'}) ha pulsado el Botón de Pánico.
               </p>
               {globalSOS.usuarios?.celular && (
                 <p className="text-white/90 text-xs font-medium mb-3">
                   Contáctalo rápido al celular: <a href={`tel:${globalSOS.usuarios.celular}`} className="font-black text-white hover:underline drop-shadow-md bg-white/20 px-1 py-0.5 rounded">{globalSOS.usuarios.celular}</a>
                 </p>
               )}
               {globalSOS.latitud !== 0 && (
                 <a href={`https://maps.google.com/?q=${globalSOS.latitud},${globalSOS.longitud}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-3 bg-white text-red-600 px-3 py-1.5 rounded-full text-[11px] font-black uppercase hover:bg-red-50 transition-colors">
                   📍 Ver Ubicación de Colisión/Rescate
                 </a>
               )}
             </div>
          </div>
        </div>
      )}

      {/* CABECERA */}
      <div className={`pt-6 px-5 pb-5 bg-black border-b border-white/5 shadow-xl flex justify-between items-center z-40 relative ${globalSOS ? 'mt-32' : ''} transition-all`}>
        <div className="flex flex-col justify-center -ml-2">
          <img src="logo.png" alt="MOTO Pīkis VPro" className="h-[65px] w-auto object-contain" />
        </div>
        
        <div className="flex flex-col items-end gap-2">
          {/* Fila 1: Campanita + Hazte ORO */}
          <div className="flex items-center gap-2">
            {(role === 'motero' || role === 'motero_dueño') && (
              <button 
                onClick={() => setMostrarBandejaOfertas(true)}
                className="relative bg-slate-800/80 backdrop-blur-md border border-white/10 p-2 rounded-full hover:bg-white/10 transition-all active:scale-95 shadow-[0_0_15px_rgba(255,255,255,0.05)]"
              >
                <Bell className="w-4 h-4 text-white" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger-red opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-danger-red border border-bg-dark"></span>
                  </span>
                )}
              </button>
            )}

            {/* Botón Upgrade a Premium */}
            {(role === 'motero' || role === 'motero_dueño') && (
              <button 
                onClick={() => {
                  if (esOroApp) setMostrarModalOro(true);
                  else setShowingMembresia(true);
                }}
                className={`border rounded-full flex items-center justify-center transition-all active:scale-95 shadow-[0_0_15px_rgba(251,191,36,0.15)] ${esOroApp ? 'bg-gold-cert/20 hover:bg-gold-cert/30 border-gold-cert p-2' : 'bg-gradient-to-tr from-yellow-500/20 to-gold-cert/10 border-gold-cert/50 hover:bg-gold-cert/20 px-3 py-1.5 gap-2'}`}
              >
                <Crown className="w-4 h-4 text-gold-cert" />
                {!esOroApp && (
                  <span className="text-[10px] font-black tracking-widest uppercase text-white/90 drop-shadow-md">Hazte ORO</span>
                )}
              </button>
            )}

            {/* Avatar de Perfil (Acceso rápido) */}
            {(role === 'motero' || (role === 'motero_dueño' && loginMode !== 'business')) && (
              <button 
                onClick={() => setActiveTab('onboarding')}
                className={`relative w-9 h-9 rounded-full border-2 transition-all active:scale-95 shadow-lg overflow-hidden ${activeTab === 'onboarding' ? 'border-primary-cyan shadow-[0_0_15px_rgba(0,168,255,0.3)]' : 'border-white/10'}`}
              >
                {avatarTabUrl ? (
                  <img src={avatarTabUrl} alt="P" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-500" />
                  </div>
                )}
              </button>
            )}
          </div>
          
          {/* Botón Mi Taller (Solo Dueño) */}
          {role === 'motero_dueño' && (
            <button 
              onClick={() => setActiveTab('taller')}
              className="relative bg-gradient-to-r from-blue-600/30 to-primary-cyan/10 backdrop-blur-md border border-primary-cyan/40 px-4 py-1.5 rounded-full flex items-center gap-2 hover:bg-primary-cyan/20 transition-all active:scale-95 shadow-[0_0_15px_rgba(0,168,255,0.2)] mt-1"
            >
              <Wrench className="w-4 h-4 text-primary-cyan" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest drop-shadow-md">Mi Taller</span>
              {alertasTaller > 0 && (
                 <span className="absolute -top-1.5 -right-1.5 bg-danger-red text-white text-[9px] font-black rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center border border-bg-dark shadow-[0_0_10px_rgba(239,68,68,0.8)] z-10 transition-transform hover:scale-110">
                    {alertasTaller}
                 </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* OVERLAY DE MONETIZACIÓN (MEMBRESIA) */}
      <Suspense fallback={null}>
        {showingMembresia && <MembresiaScreen onClose={() => setShowingMembresia(false)} />}
      </Suspense>

      {/* MODAL PREMIUM ORO (YA SOY ORO) */}
      <AnimatePresence>
        {mostrarModalOro && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[500] bg-black/80 backdrop-blur-xl flex items-center justify-center p-6"
            onClick={() => setMostrarModalOro(false)}
          >
            <motion.div 
              initial={{ scale: 0.8, opacity: 0, rotate: -2 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              exit={{ scale: 0.8, opacity: 0, rotate: 2 }}
              className="bg-[#151515] border-2 border-gold-cert/40 rounded-[40px] p-8 w-full max-w-[340px] text-center shadow-[0_0_80px_rgba(251,191,36,0.25)] flex flex-col items-center relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
               {/* Decoración de fondo */}
               <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-gold-cert to-transparent opacity-50"></div>
               
               <div className="w-24 h-24 bg-gold-cert/10 rounded-full flex items-center justify-center mb-6 border border-gold-cert/30 shadow-[0_0_30px_rgba(251,191,36,0.15)] relative">
                  <div className="absolute inset-0 rounded-full animate-ping bg-gold-cert/5"></div>
                  <div className="w-16 h-16 bg-gradient-to-br from-gold-cert to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
                    <Crown className="w-9 h-9 text-bg-dark" />
                  </div>
               </div>
               
               <h3 className="text-gold-cert font-black text-2xl uppercase tracking-[0.15em] mb-3 drop-shadow-[0_2px_10px_rgba(251,191,36,0.4)]">Estatus Oro</h3>
               <p className="text-slate-200 text-sm font-bold leading-relaxed mb-8 px-2">
                 ¡Felicidades! <br/>
                 <span className="text-white/60 font-medium text-xs mt-2 block">Ya disfrutas de todos los beneficios exclusivos del ecosistema MOTOPIKIS Pro.</span>
               </p>

               <button 
                 onClick={() => setMostrarModalOro(false)}
                 className="w-full bg-gradient-to-b from-yellow-400 to-gold-cert text-bg-dark font-black text-[14px] uppercase tracking-widest py-4 rounded-2xl shadow-[0_4px_20px_rgba(251,191,36,0.5)] transition-all active:scale-95 hover:brightness-110"
               >
                 Aceptar
               </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* OVERLAY DE CONFIGURACION SOS */}
      <Suspense fallback={null}>
        {showingSOSConfig && <SOSConfigModal onClose={() => setShowingSOSConfig(false)} themeClass={getThemeBackground()} />}
      </Suspense>

      {/* OVERLAY DE APODOS DE VEHICULOS */}
      <Suspense fallback={null}>
        {showingApodos && <ApodosModal onClose={() => setShowingApodos(false)} themeClass={getThemeBackground()} />}
      </Suspense>

      {/* OVERLAY DE AYUDA / MANUAL */}
      <Suspense fallback={null}>
        {showingAyuda && <AyudaScreen onClose={() => setShowingAyuda(false)} />}
      </Suspense>

      {/* POP-UP GIGANTE TIPO LLAMADA */}
      <AnimatePresence>
        {popupOferta && !popupOferta.tipo && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-sm flex flex-col justify-center px-6"
          >
            <div className="bg-gradient-to-b from-[#1e293b] to-black border border-primary-cyan/40 rounded-3xl p-6 relative shadow-[0_0_50px_rgba(0,168,255,0.2)] text-center w-full max-w-sm mx-auto">
              <div className="w-20 h-20 bg-primary-cyan/20 rounded-full flex items-center justify-center border-2 border-primary-cyan/50 mx-auto mb-5 animate-pulse shadow-[0_0_30px_rgba(0,168,255,0.4)]">
                <MessageSquare className="w-10 h-10 text-primary-cyan" />
              </div>

              <h2 className="text-xl font-black text-white leading-tight mb-2 uppercase">¡Nueva Cotización!</h2>
              <p className="text-sm text-slate-300 font-medium mb-6 leading-tight">El <strong className="text-white">{popupOferta.taller}</strong> ha respondido a tu varada/solicitud.</p>
              
              <div className="bg-black/50 border border-white/10 rounded-2xl py-4 mb-6 flex flex-col items-center">
                <span className="text-[10px] text-primary-cyan font-black uppercase tracking-widest block mb-1">Precio Ofertado</span>
                <span className="text-3xl font-black text-white block">$ {popupOferta.precio.toLocaleString('es-CO')}</span>
                <p className="text-text-muted text-[11px] mt-2 px-4 leading-tight italic">"{popupOferta.detalle}"</p>
              </div>

              <div className="space-y-3">
                <button 
                  onClick={() => { setPopupOferta(null); setMostrarBandejaOfertas(true); }}
                  className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 font-extrabold text-white text-[13px] tracking-wide rounded-xl shadow-[0_10px_20px_rgba(16,185,129,0.3)] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 uppercase"
                >
                  <MessageCircle className="w-5 h-5" /> Revisar y Negociar
                </button>
                <button 
                  onClick={() => setPopupOferta(null)}
                  className="w-full py-4 bg-white/5 font-bold text-slate-400 text-[12px] rounded-xl hover:bg-white/10 active:scale-95 transition-all uppercase tracking-wider"
                >
                  Cerrar por ahora
                </button>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* MINI FICHA DEL TALLER DESDE LA BANDEJA */}
        {popupOferta && popupOferta.tipo === 'ficha' && (
          <motion.div 
             initial={{ opacity: 0, y: '50%' }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: '50%' }}
             className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-sm p-6 px-4 overflow-y-auto"
          >
            <button onClick={() => setPopupOferta(null)} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 active:scale-90 transition-all"><X className="w-5 h-5 text-white" /></button>
            <div className="mt-12 text-center relative max-w-sm mx-auto w-full">
               <Store className="w-16 h-16 text-gold-cert mx-auto mb-4 filter drop-shadow-[0_0_15px_rgba(251,191,36,0.3)]" />
               <h2 className="text-2xl font-black text-white px-4 leading-none mb-1">{popupOferta.data.nombre}</h2>
               <p className="text-gold-cert text-xs mt-1 uppercase tracking-widest font-black mb-6">Score Oro: {popupOferta.data.rating}</p>
               
               <div className="bg-slate-800/80 p-5 rounded-2xl border border-white/10 space-y-5 text-left mb-6 shadow-xl">
                  <div>
                    <strong className="block text-primary-cyan text-[10px] uppercase tracking-widest mb-1 opacity-70">Especialidad Principal</strong>
                    <p className="text-sm font-bold text-white leading-snug border-l-2 border-primary-cyan pl-2">{popupOferta.data.especialidad || 'Mecánica General'}</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <strong className="block text-primary-cyan text-[10px] uppercase tracking-widest mb-1 opacity-70">Ciudad Base</strong>
                      <p className="text-[13px] font-bold text-white border-l-2 border-white/10 pl-2">{popupOferta.data.ciudad || 'No definida'}</p>
                    </div>
                    <div className="flex-1">
                      <strong className="block text-primary-cyan text-[10px] uppercase tracking-widest mb-1 opacity-70">Estado Negocio</strong>
                      <p className={`text-[13px] font-bold border-l-2 pl-2 ${popupOferta.data.open ? 'text-green-400 border-green-500/50' : 'text-danger-red border-danger-red/50'}`}>{popupOferta.data.open ? 'Operando' : 'Cerrado/Ocupado'}</p>
                    </div>
                  </div>
                  <div>
                    <strong className="block text-primary-cyan text-[10px] uppercase tracking-widest mb-1 opacity-70">Dirección o Referencia</strong>
                    <p className="text-[13px] font-medium text-white break-words border-l-2 border-white/10 pl-2 opacity-90">{popupOferta.data.direccion || 'Taller móvil / Sin dirección'}</p>
                  </div>
               </div>
               
               <button onClick={() => setPopupOferta(null)} className="w-full py-3.5 bg-white/10 hover:bg-white/20 active:scale-95 transition-all border border-white/10 rounded-xl text-white font-bold tracking-widest text-[12px] uppercase">
                 Cerrar Ficha
               </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* POP-UP AUDITORIA (EL MOTERO VALIDA) */}
      <AnimatePresence>
        {modalAuditoria && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-sm flex flex-col justify-center px-6"
          >
            <div className="bg-gradient-to-b from-slate-900 to-black border border-gold-cert/30 rounded-3xl p-6 relative shadow-[0_0_50px_rgba(251,191,36,0.15)] text-center w-full max-w-sm mx-auto">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold-cert/5 rounded-full blur-2xl pointer-events-none -mr-10 -mt-10"></div>
              
              <div className="w-20 h-20 bg-gold-cert/10 rounded-full flex items-center justify-center border border-gold-cert/30 mx-auto mb-5 shadow-[0_0_20px_rgba(251,191,36,0.2)]">
                <Store className="w-10 h-10 text-gold-cert" />
              </div>

              <h2 className="text-xl font-black text-white leading-tight mb-2 uppercase">Servicio Finalizado</h2>
              <p className="text-[13px] text-slate-300 font-medium mb-6 leading-tight">El negocio <strong className="text-gold-cert">{modalAuditoria.taller}</strong> reporta haber terminado el servicio con éxito.</p>
              
              <div className="bg-black/50 border border-white/10 rounded-2xl py-5 mb-6 flex flex-col items-center">
                <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest block mb-1">Valor Cobrado según el Taller</span>
                <span className="text-3xl font-black text-white block">$ {modalAuditoria.precio.toLocaleString('es-CO')}</span>
              </div>

              <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-xl mb-6 text-left">
                <p className="text-[11px] text-green-400 font-bold mb-1 flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> GANA +{Math.floor(modalAuditoria.precio / 1000)} PUNTOS EXPERIENCIA</p>
                <p className="text-[11px] text-slate-300 leading-tight">Confirma o corrige este valor. Tu honestidad blinda a la comunidad y te hace ganar puntos.</p>
              </div>

              {auditMontoLibre ? (
                 <div className="space-y-4 mb-2 animate-pulse-fast transition-all">
                   <p className="text-white text-xs font-bold mb-2">Por favor, escribe el valor exacto que cancelaste:</p>
                   <div className="relative">
                      <span className="absolute left-4 top-[17px] text-slate-500 font-bold">$</span>
                      <input 
                        type="tel"
                        value={auditValorManual}
                        onChange={e => {
                           let raw = e.target.value.replace(/\D/g, '');
                           if (raw === '') { setAuditValorManual(''); return; }
                           setAuditValorManual(Number(raw).toLocaleString('es-CO'));
                        }}
                        autoFocus
                        placeholder="150.000"
                        className="w-full p-4 pl-8 rounded-xl border border-gold-cert/50 bg-black text-white font-bold text-lg outline-none focus:border-gold-cert focus:shadow-[0_0_15px_rgba(251,191,36,0.3)] transition-all"
                      />
                   </div>
                   <div className="flex gap-2 mt-4">
                      <button 
                        onClick={() => { setAuditMontoLibre(false); setAuditValorManual(''); }}
                        className="w-1/3 py-3 bg-white/5 text-slate-400 font-bold text-[11px] uppercase tracking-wider rounded-xl hover:bg-white/10"
                      >
                        Volver
                      </button>
                      <button 
                        onClick={async () => {
                           const rawStr = auditValorManual ? auditValorManual.toString().replace(/\D/g, '') : '';
                           if (rawStr === '') return alert("Ingresa un valor numérico (puedes poner 0 si el negocio se cayó).");
                           const val = parseFloat(rawStr);
                           try {
                               await supabase.from('cotizaciones').update({ auditor_motero: true, valor_motero: val }).eq('id', modalAuditoria.id);
                               let ptos = Math.floor(val / 1000);
                               if (val === 0) {
                                  alert("Has confirmado que el negocio no se concretó (cobro de $0). No se sumaron Puntos VIP en esta ocasión porque no hubo factura.");
                               } else {
                                  alert(`Auditoría generada exitosamente. Investigaremos la discrepancia en el valor, ¡tienes ${ptos} puntos por reportarlo!`); 
                               }
                               setModalAuditoria(null); 
                               setAuditMontoLibre(false); setAuditValorManual('');
                           } catch(e) { alert("Error de conexión."); }
                        }}
                        className="flex-1 py-3 bg-gradient-to-r from-gold-cert to-yellow-600 font-extrabold text-bg-dark text-[11px] tracking-wide rounded-xl shadow-[0_10px_20px_rgba(251,191,36,0.2)] hover:scale-[1.02] active:scale-95 transition-all uppercase flex justify-center items-center"
                      >
                        Enviar Reporte
                      </button>
                   </div>
                 </div>
              ) : (
                <div className="space-y-3">
                  <button 
                    onClick={async () => { 
                      try {
                          await supabase.from('cotizaciones').update({ auditor_motero: true, valor_motero: modalAuditoria.precio }).eq('id', modalAuditoria.id);
                          alert(`¡Gracias por tu honestidad! Has ganado ${Math.floor(modalAuditoria.precio / 1000)} Puntos de Experiencia. El valor ha sido reportado exitosamente.`); 
                          setModalAuditoria(null); 
                      } catch(e) { alert("Error de conexión."); }
                    }}
                    className="w-full py-4 bg-gradient-to-r from-gold-cert to-yellow-600 font-extrabold text-bg-dark text-[13px] tracking-wide rounded-xl shadow-[0_10px_20px_rgba(251,191,36,0.2)] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 uppercase"
                  >
                    <CheckCircle className="w-5 h-5 flex-shrink-0" /> Confirmar: Pagué eso
                  </button>
                  <button 
                    onClick={() => setAuditMontoLibre(true)}
                    className="w-full py-3 bg-red-500/10 border border-red-500/20 font-black text-red-400 text-[11px] rounded-xl hover:bg-red-500/20 active:scale-95 transition-all uppercase tracking-wider"
                  >
                    Corregir / Pagué otra cantidad
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* BANDEJA DE OFERTAS MODAL */}
      <AnimatePresence>
        {mostrarBandejaOfertas && (
          <motion.div 
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            className="fixed inset-0 z-[300] bg-[#0f172a]/95 backdrop-blur-md flex flex-col pt-10 px-5"
          >
            <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
               <div>
                  <h2 className="text-2xl font-black text-white flex items-center gap-2">
                    <Bell className="w-6 h-6 text-primary-cyan" />
                    Mis Ofertas
                  </h2>
                  <p className="text-[10px] text-primary-cyan font-bold tracking-widest uppercase mt-1">Cotizaciones Entrantes</p>
               </div>
               <button 
                 onClick={() => { setMostrarBandejaOfertas(false); setOfertas(ofertas.map(o => ({...o, leido: true}))); }} 
                 className="p-2 bg-white/5 rounded-full hover:bg-white/10 flex items-center justify-center text-white transition-colors"
               >
                 <X className="w-5 h-5" />
               </button>
            </div>

            <div className="flex-1 overflow-y-auto pb-20 pt-2 space-y-8">
               {/* SECCIÓN 1: SOLICITUDES ACTIVAS DEL USUARIO */}
               {solicitudesActivas.length > 0 && (
               <div>
                 <h3 className="text-primary-cyan font-bold text-[10px] uppercase tracking-widest mb-3 flex items-center gap-1.5"><PackageOpen className="w-4 h-4" /> Tus Solicitudes en curso</h3>
                 <div className="space-y-4">
                 {solicitudesActivas.map(sol => (
                 <div key={sol.id} className="bg-slate-800/40 border border-dashed border-slate-700 rounded-2xl p-4 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-primary-cyan/50"></div>
                    <div className="flex justify-between items-start mb-2 pl-2">
                       <h4 className="font-black text-white text-[13px] pr-2">{"Licitación: " + (sol.detalle_solicitud?.length > 30 ? sol.detalle_solicitud.substring(0,30) + '...' : sol.detalle_solicitud)}</h4>
                       <div className="px-2 py-0.5 bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 text-[9px] uppercase font-black rounded tracking-widest animate-pulse shrink-0">En Espera</div>
                    </div>
                    <div className="pl-2 flex justify-between items-end">
                       <div className="flex-1 mr-4">
                          <p className="text-[10px] text-slate-500 mb-2">Notificando a talleres cercanos...</p>
                          <div className="h-1.5 w-full bg-slate-900 rounded-full overflow-hidden">
                             <div className="h-full bg-gradient-to-r from-primary-cyan/20 to-primary-cyan w-2/3 animate-pulse rounded-full relative">
                                <div className="absolute top-0 right-0 w-2 h-full bg-white blur-[2px]"></div>
                             </div>
                          </div>
                          <p className="text-[9px] text-primary-cyan/70 mt-1.5">Buscando en un radio de 5km</p>
                       </div>
                       <button 
                         onClick={async () => {
                            if (confirm("¿Deseas cancelar esta solicitud de auxilio?")) {
                               await supabase.from('cotizaciones').delete().eq('id', sol.id);
                               setSolicitudesActivas(solicitudesActivas.filter(s => s.id !== sol.id));
                            }
                         }}
                         className="p-2 border border-danger-red/30 text-danger-red rounded-full hover:bg-danger-red hover:text-white transition-colors"
                         title="Cancelar Solicitud"
                       >
                         <X className="w-3 h-3" />
                       </button>
                    </div>
                 </div>
                 ))}
                 </div>
               </div>
               )}

               {/* SECCIÓN 2: COTIZACIONES RESPONDIDAS */}
               <div>
                 <h3 className="text-gold-cert font-bold text-[10px] uppercase tracking-widest mb-3 flex items-center gap-1.5"><Store className="w-4 h-4" /> Cotizaciones Recibidas</h3>
                 <div className="space-y-4">
               {ofertas.length === 0 ? (
                 <div className="flex flex-col items-center justify-center text-center mt-20 opacity-50">
                    <MessageSquare className="w-12 h-12 text-slate-500 mb-4" />
                    <p className="font-bold text-slate-400 text-lg">Buzón Vacío</p>
                    <p className="text-xs text-slate-500 mt-1">Aún no hay cotizaciones de talleres.</p>
                 </div>
               ) : (
                 ofertas.map(oferta => (
                    <div key={oferta.id} className="bg-slate-800/80 border border-slate-700 rounded-2xl p-4 relative shadow-lg">
                       {!oferta.leido && <div className="absolute top-4 right-4 w-2.5 h-2.5 rounded-full bg-danger-red animate-pulse border border-bg-dark"></div>}
                       <div className="flex justify-between items-start mb-1">
                          <h3 className="font-black text-[15px] text-white flex items-center gap-2">
                             <Store className="w-4 h-4 text-gold-cert" /> {oferta.taller}
                          </h3>
                          <button 
                            onClick={e => eliminarCotizacion(e, oferta.id)} 
                            className="text-danger-red/60 hover:text-danger-red transition-colors p-1 active:scale-90"
                            title="Eliminar Oferta"
                          >
                             <Trash2 className="w-4 h-4" />
                          </button>
                       </div>
                       <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-3 border-b border-white/5 pb-2">Recibida a las {oferta.fecha}</p>
                       <span className="text-2xl font-black text-white block mb-2">$ {oferta.precio.toLocaleString('es-CO')}</span>
                       <p className="text-[12px] text-slate-300 italic mb-4 border-l-2 border-primary-cyan pl-2 leading-tight">"{oferta.detalle}"</p>
                       <div className="flex gap-2">
                         <button onClick={() => verFicha(oferta)} className="flex-1 bg-white/5 hover:bg-white/10 transition-colors py-2.5 rounded-xl text-xs font-bold text-white border border-white/10 flex items-center justify-center gap-1 active:scale-95">
                           <Store className="w-4 h-4" /> Ver Ficha
                         </button>
                         <button onClick={() => contactarWhatsApp(oferta)} className="flex-1 bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500 hover:text-white transition-colors py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95">
                           <MessageCircle className="w-4 h-4" /> Whatsapp
                         </button>
                       </div>
                    </div>
                 ))
               )}
               </div>
               </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* POP-UP GLOBAL DE INVITACIÓN DIRECTA A RODADA (?unirse) */}
      <AnimatePresence>
        {rutaAConfirmar && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-5"
          >
            <div className="w-full max-w-sm bg-gradient-to-br from-[#1e293b] to-[#0f172a] border border-primary-cyan/40 rounded-3xl p-6 relative shadow-[0_0_80px_rgba(0,168,255,0.3)]">
               <button onClick={() => setRutaAConfirmar(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white p-2">
                 <X className="w-5 h-5" />
               </button>
               <div className="w-16 h-16 bg-primary-cyan/10 rounded-full flex items-center justify-center mb-4 mx-auto border border-primary-cyan/30">
                  <CheckCircle className="w-8 h-8 text-primary-cyan shadow-[0_0_20px_rgba(0,168,255,0.8)]" />
               </div>
               <h2 className="text-white font-black text-xl text-center uppercase tracking-wide mb-1 leading-tight">Invitación a Rodada</h2>
               <p className="text-slate-400 text-[11px] text-center font-bold mb-5 tracking-widest uppercase">{rutaAConfirmar.nombre}</p>
               
               <div className="bg-black/40 border border-white/5 rounded-2xl p-4 mb-6 shadow-inner space-y-3">
                  <div>
                    <span className="block text-[9px] text-slate-500 font-bold uppercase mb-1">Punto de Encuentro</span>
                    <span className="block text-white font-black text-xs uppercase">{rutaAConfirmar.puntoEncuentro}</span>
                  </div>
                  <div>
                    <span className="block text-[9px] text-slate-500 font-bold uppercase mb-1">Destino Final</span>
                    <span className="block text-primary-cyan font-black text-sm uppercase">{rutaAConfirmar.destino}</span>
                  </div>
               </div>

               <button 
                 onClick={handleConfirmarAsistencia}
                 className="w-full bg-primary-cyan text-black hover:bg-white border-2 border-transparent font-black py-4 rounded-xl text-sm uppercase shadow-[0_0_20px_rgba(0,168,255,0.4)] transition-all flex justify-center tracking-widest"
               >
                 Confirmar Asistencia
               </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* POP-UP GLOBAL DE ANUNCIOS PUSH IN-APP */}
      <AnimatePresence>
        {anuncioInApp && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[10000] bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-5"
          >
            <div className="w-full max-w-sm bg-gradient-to-br from-[#1e293b] to-[#0f172a] border-2 border-primary-cyan/80 rounded-3xl p-6 relative shadow-[0_0_80px_rgba(0,168,255,0.5)]">
               <div className="w-16 h-16 bg-primary-cyan/20 rounded-full flex items-center justify-center mb-4 mx-auto border border-primary-cyan shadow-[0_0_20px_rgba(0,168,255,0.8)]">
                  <Bell className="w-8 h-8 text-primary-cyan animate-pulse" />
               </div>
               <h2 className="text-white font-black text-xl text-center uppercase tracking-wider mb-2 leading-tight">Tienes un Mensaje</h2>
               <p className="text-slate-400 text-[11px] text-center font-bold mb-5 tracking-widest uppercase">
                 De <span className="text-white bg-white/10 px-2 py-0.5 rounded">{anuncioInApp.emisor_nombre}</span>
               </p>
               
               <div className="bg-black/40 border border-white/5 rounded-2xl p-5 mb-6 shadow-inner">
                  <p className="text-white font-medium text-sm leading-relaxed text-center italic">
                    "{anuncioInApp.mensaje}"
                  </p>
               </div>

               <button 
                 onClick={() => setAnuncioInApp(null)}
                 className="w-full bg-primary-cyan text-bg-dark hover:bg-white border-2 border-transparent font-black py-4 rounded-xl text-sm uppercase shadow-[0_0_20px_rgba(0,168,255,0.4)] transition-all flex justify-center tracking-widest"
               >
                 Entendido
               </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* BLOQUEO DE SOS MÍO ACTIVO (Punto de Control) */}
      <AnimatePresence>
        {bloqueoMiSOS && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[11000] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center p-6"
          >
            <div className="bg-yellow-500/20 border-2 border-yellow-500 rounded-3xl p-8 w-full max-w-sm flex flex-col items-center shadow-[0_0_100px_rgba(234,179,8,0.4)]">
              <div className="w-24 h-24 bg-yellow-500 rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(234,179,8,0.8)] animate-pulse">
                 <AlertTriangle className="w-12 h-12 text-bg-dark" />
              </div>
              <h1 className="text-white font-black text-2xl text-center uppercase tracking-widest mb-3">Punto de Control</h1>
              <p className="text-yellow-400 font-bold text-center text-sm leading-relaxed mb-8">
                Tu Botón de Pánico (S.O.S) sigue ENCENDIDO desde tu viaje anterior.
              </p>
              <div className="flex flex-col gap-3 w-full">
                <button 
                  onClick={cancelarMiSOSPendiente}
                  className="w-full bg-yellow-500 text-bg-dark font-black py-4 rounded-xl shadow-[0_0_20px_rgba(234,179,8,0.5)] active:scale-95 transition-all text-sm uppercase tracking-widest"
                >
                  Sí, estoy a salvo
                </button>
                <button 
                  onClick={() => setBloqueoMiSOS(null)}
                  className="w-full bg-red-600 text-white font-black py-4 rounded-xl border border-red-500 active:scale-95 transition-all text-sm uppercase tracking-widest shadow-[0_0_20px_rgba(239,68,68,0.4)]"
                >
                  Sigo en Emergencia
                </button>
              </div>
              <p className="text-slate-500 text-[10px] text-center mt-6 uppercase tracking-widest">Selecciona tu estado real de seguridad.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CUERPO DE LA APP */}
      <div id="scroll-body" className="flex-1 px-5 pt-5 pb-8 overflow-y-auto relative scrollbar-hide min-h-0 w-full">
        <Suspense fallback={<ScreenLoader />}>
          {activeTab === 'onboarding' && (
            <OnboardingScreen 
              onFinish={() => setActiveTab('expediente')} 
              appTheme={appTheme}
              setAppTheme={setAppTheme}
              onOpenSOSConfig={() => setShowingSOSConfig(true)}
              onOpenApodos={() => setShowingApodos(true)}
              onOpenNotificaciones={() => setMostrarBandejaOfertas(true)}
              onOpenAyuda={() => setShowingAyuda(true)}
              unreadCount={unreadCount}
            />
          )}
          {activeTab === 'expediente' && <ExpedienteScreen themeClass={getThemeBackground()} onOpenMembresia={() => setShowingMembresia(true)} onOpenTaller={() => setActiveTab('taller')} role={role} />}
          {activeTab === 'sos' && <SOSScreen onOpenConfig={() => setShowingSOSConfig(true)} />}
          {activeTab === 'mercado' && <MercadoScreen />}
          {activeTab === 'taller' && <TallerScreen />}
          {activeTab === 'destinos' && <DestinosScreen />}
          {activeTab === 'parche' && <ParcheScreen onOpenMembresia={() => setShowingMembresia(true)} />}
          {activeTab === 'murobiker' && <MuroBikerScreen />}
          {activeTab === 'motomarket' && (
            <MotomarketScreen 
              onOpenExpediente={() => setActiveTab('expediente')} 
              idAutoOpen={adAVisitar} 
              onAdOpened={() => setAdAVisitar(null)}
            />
          )}
          {activeTab === 'configuracion' && (
            <ConfiguracionScreen 
               appTheme={appTheme}
               setAppTheme={setAppTheme}
               onOpenSOSConfig={() => setShowingSOSConfig(true)}
               onOpenApodos={() => setShowingApodos(true)}
               onOpenNotificaciones={() => setMostrarBandejaOfertas(true)}
               onOpenAyuda={() => setShowingAyuda(true)}
               unreadCount={unreadCount}
               onOpenCrearPIN={() => {}}
            />
          )}
          {showingAyuda && <AyudaScreen onClose={() => setShowingAyuda(false)} />}
        </Suspense>
        
        {/* Espaciador físico para botón SOS */}
        <div className="h-14 w-full shrink-0"></div>
      </div>

      {/* NAVEGACIÓN INFERIOR CONDICIONADA POR ROL */}
      <div className="bg-[#1e293b]/95 backdrop-blur-md pb-8 pt-4 px-2 flex justify-around border-t border-white/10 z-40 rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.5)] shrink-0 w-full mt-auto">
        
        {/* PESTAÑAS PARA MOTEROS Y MOTEROS DUEÑOS EN MODO NORMAL */}
        {(role === 'motero' || (role === 'motero_dueño' && loginMode !== 'business')) && (
          <>
            <button 
              onClick={() => handleTabChange('expediente')}
              className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors ${activeTab === 'expediente' ? 'text-primary-cyan' : 'text-text-muted hover:text-text-main'}`}
            >
              <Bike className="w-4 h-4 shrink-0" />
              <span className="truncate w-full text-center px-0.5">Garaje</span>
            </button>

            <button 
              onClick={() => handleTabChange('murobiker')}
              className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors ${activeTab === 'murobiker' ? 'text-primary-cyan' : 'text-text-muted hover:text-text-main'}`}
            >
              <Layout className="w-4 h-4 shrink-0" />
              <span className="truncate w-full text-center px-0.5">Muro Biker</span>
            </button>

            <button 
              onClick={() => handleTabChange('destinos')}
              className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors ${activeTab === 'destinos' ? 'text-primary-cyan' : 'text-text-muted hover:text-text-main'}`}
            >
              <Map className="w-4 h-4 shrink-0" />
              <span className="truncate w-full text-center px-0.5">Rutas</span>
            </button>

    <button 
      onClick={() => handleTabChange('sos')}
      className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-black uppercase transition-all relative ${activeTab === 'sos' ? 'text-white' : 'text-red-500 hover:text-red-400'}`}
    >
      <div className="absolute -top-10 bg-gradient-to-b from-red-500 to-red-600 p-4 rounded-full shadow-[0_0_30px_rgba(239,68,68,0.7)] border-[5px] border-red-950 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center shrink-0">
        <AlertOctagon className="w-7 h-7 text-white drop-shadow-md shrink-0" />
      </div>
      <span className="mt-10 truncate w-full text-center px-0.5">S.O.S</span>
    </button>

    <button 
      onClick={() => handleTabChange('parche')}
      className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors ${activeTab === 'parche' ? 'text-primary-cyan' : 'text-text-muted hover:text-text-main'}`}
    >
      <Users className="w-4 h-4 shrink-0" />
      <span className="truncate w-full text-center px-0.5">Parche</span>
    </button>

    <button 
      onClick={() => handleTabChange('mercado')}
      className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors ${activeTab === 'mercado' ? 'text-primary-cyan' : 'text-text-muted hover:text-text-main'}`}
    >
      <Store className="w-4 h-4 shrink-0" />
      <span className="truncate w-full text-center px-0.5">Talleres</span>
    </button>
  </>
)}

{/* PESTAÑA LICITACIONES / SALIR (Rol Empleado) */}
{role === 'empleado_taller' && (
  <>
    <button 
      onClick={() => handleTabChange('taller')}
      className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors ${activeTab === 'taller' ? 'text-gold-cert' : 'text-text-muted hover:text-gold-cert'}`}
    >
      <div className="relative">
        <Wrench className="w-4 h-4 shrink-0" />
      </div>
      <span className="truncate w-full text-center px-0.5">Licitaciones</span>
    </button>
    <button 
      onClick={() => handleTabChange('motomarket')}
      className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors relative ${activeTab === 'motomarket' ? 'text-primary-cyan' : 'text-text-muted hover:text-white'}`}
    >
      <div className="relative">
         <ShoppingBag className="w-4 h-4 shrink-0" />
      </div>
      <span className="truncate w-full text-center px-0.5">MotoMarket</span>
    </button>
    <button 
      onClick={logout}
      className="flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors text-danger-red hover:text-white"
    >
      <LogOut className="w-4 h-4 shrink-0" />
      <span className="truncate w-full text-center px-0.5">Salir</span>
    </button>
  </>
)}

{/* PESTAÑA DUEÑO EN MODO NEGOCIO (Aislada pero con MotoMarket) */}
{role === 'motero_dueño' && loginMode === 'business' && (
  <>
    <button 
      onClick={() => handleTabChange('taller')}
      className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors ${activeTab === 'taller' ? 'text-primary-cyan' : 'text-text-muted hover:text-primary-cyan'}`}
    >
      <div className="relative">
        <Wrench className="w-4 h-4 shrink-0" />
      </div>
      <span className="truncate w-full text-center px-0.5">Mi Taller</span>
    </button>

    <button 
      onClick={() => handleTabChange('motomarket')}
      className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors relative ${activeTab === 'motomarket' ? 'text-primary-cyan' : 'text-text-muted hover:text-white'}`}
    >
      <div className="relative">
         <ShoppingBag className="w-4 h-4 shrink-0" />
      </div>
      <span className="truncate w-full text-center px-0.5">MotoMarket</span>
    </button>

    <button 
      onClick={() => handleTabChange('configuracion')}
      className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors relative ${activeTab === 'configuracion' ? 'text-primary-cyan' : 'text-text-muted hover:text-white'}`}
    >
      <div className="relative">
         <Lock className="w-4 h-4 shrink-0" />
      </div>
      <span className="truncate w-full text-center px-0.5">Clave</span>
    </button>

    <button 
      onClick={logout}
      className="flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors text-danger-red hover:text-white"
    >
      <LogOut className="w-4 h-4 shrink-0" />
      <span className="truncate w-full text-center px-0.5">Salir</span>
    </button>
  </>
)}

{/* Botón de MotoMarket (Global para Moteros y Dueños en modo normal) */}
{(role === 'motero' || (role === 'motero_dueño' && loginMode !== 'business')) && (
  <button 
    onClick={() => handleTabChange('motomarket')}
    className={`flex-1 flex flex-col items-center gap-1 text-[8px] sm:text-[10px] tracking-tighter font-semibold uppercase transition-colors relative ${activeTab === 'motomarket' ? 'text-primary-cyan' : 'text-text-muted hover:text-white'}`}
  >
    <div className="relative">
       <ShoppingBag className="w-4 h-4 shrink-0" />
    </div>
    <span className="truncate w-full text-center px-0.5">MotoMarket</span>
  </button>
)}

      </div>
      
      {/* Modal de Confirmación de Cierre de Sesión */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-sm bg-[#0B0F19] border border-white/10 rounded-[30px] p-8 text-center shadow-2xl relative overflow-hidden"
            >
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50"></div>
              
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-500/20">
                <LogOut className="w-8 h-8 text-red-400" />
              </div>
              
              <h3 className="text-white font-black text-xl mb-3 tracking-wide">¿Cerrar Sesión?</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-8">
                ¿Seguro que deseas cerrar sesión y salir del menú de administración?
              </p>
              
              <div className="flex gap-4">
                <button 
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-black rounded-xl text-xs uppercase tracking-[0.2em] transition-all active:scale-95"
                >
                  Cancelar
                </button>
                <button 
                  onClick={confirmLogout}
                  className="flex-1 py-4 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 font-black rounded-xl text-xs uppercase tracking-[0.2em] transition-all active:scale-95"
                >
                  Salir
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* BANNER DE ACTUALIZACIÓN DISPONIBLE */}
      <AnimatePresence>
        {swRegistration && (
          <motion.div 
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            className="fixed top-0 left-0 right-0 z-[500] p-4 flex justify-center pointer-events-none"
          >
            <div className="bg-primary-cyan border-b-4 border-black/20 text-white p-4 rounded-2xl shadow-[0_10px_40px_rgba(0,168,255,0.4)] flex flex-col items-center gap-3 w-full max-w-sm pointer-events-auto border border-white/20">
               <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-full animate-pulse">
                    <RefreshCw className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-black text-[13px] uppercase tracking-tighter leading-none">Nueva Versión Pro</span>
                    <span className="text-[10px] font-bold text-white/80 uppercase tracking-widest mt-1">Mejoras listas para rodar</span>
                  </div>
               </div>
               
               <button 
                  onClick={() => {
                    if (swRegistration.waiting) {
                      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
                    }
                  }}
                  className="w-full py-3 bg-black text-primary-cyan font-black text-[12px] uppercase tracking-[0.2em] rounded-xl shadow-lg hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2"
               >
                  Actualizar Ahora <CheckCircle className="w-4 h-4" />
               </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

export default App;
